export type SparklineTone = 'neutral' | 'critical' | 'warn' | 'watch' | 'healthy';

export type SparklineMiniProps = {
  series: number[];
  tone?: SparklineTone;
  width?: number;
  height?: number;
};

const STROKE: Record<SparklineTone, string> = {
  neutral: 'stroke-gray-700',
  critical: 'stroke-critical',
  warn: 'stroke-warn',
  watch: 'stroke-watch',
  healthy: 'stroke-healthy',
};

/**
 * Tiny in-line trend, no axes. Width is fixed so the rendering is stable
 * inside table rows / KPI cards. An empty series collapses to a 1px
 * horizontal rule — see components.md.
 */
export function SparklineMini({
  series,
  tone = 'neutral',
  width = 64,
  height = 18,
}: SparklineMiniProps) {
  if (series.length < 2) {
    return (
      <span
        role="img"
        aria-label="No trend data"
        className="inline-block align-middle"
        style={{ width, height }}
      >
        <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
          <line
            x1="0"
            x2={width}
            y1={height / 2}
            y2={height / 2}
            className="stroke-gray-300"
            strokeWidth="1"
          />
        </svg>
      </span>
    );
  }

  const min = Math.min(...series);
  const max = Math.max(...series);
  const range = max - min || 1;
  const stepX = width / (series.length - 1);
  const points = series
    .map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / range) * (height - 2) - 1;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');

  return (
    <span
      role="img"
      aria-label={`Sparkline, ${series.length} points, tone ${tone}`}
      className="inline-block align-middle"
      style={{ width, height }}
    >
      <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
        <polyline
          points={points}
          fill="none"
          strokeWidth="1.25"
          strokeLinejoin="round"
          strokeLinecap="round"
          className={STROKE[tone]}
        />
      </svg>
    </span>
  );
}
